import { isDefined, isEmpty } from '@sgme/fp';
import type { PersistedTab, PersistedTabsDescription } from 'apiConnector/tabs/tabs.model';
import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import type { BlotterType } from 'models/blotters/BlotterType';
import type { FieldMetadata } from 'models/forms/fields/FieldMetadata';
import { IBlotterItem } from 'models/IBlotterItem';
import { ProductVariationBase } from 'models/productsReferential';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import * as fromPriceImprovementBlotter from 'state/blotters/priceImprovement/priceImprovementBlotter.selector';
import * as fromQuoteBlotter from 'state/blotters/quote/quoteBlotter.selector';
import * as fromSecondaryMarketOrderBlotter from 'state/blotters/secondaryMarketOrderBlotter/secondaryMarketOrderBlotter.selector';
import * as fromSecondaryMarketTradeBlotter from 'state/blotters/secondaryMarketTradeBlotter/secondaryMarketTradeBlotter.selector';
import {
  selectTradesBlottersColumnDefinition,
  selectTradesBlottersType,
} from 'state/blotters/trades/tradesBlotters.selector';
import type { BdrMonthlyExpiryDatesState } from 'state/monthlyExpiryDates/monthlyExpiryDates.model';
import type { ProductGraph, ProductNode } from 'state/product-description/model';
import { getAllGimmicksDescription, selectProductDescription } from 'state/product-description/selectors';
import type { StaticProductView } from 'state/product-description/types';
import { ScreenMode } from 'state/quoteWorkspace';
import * as fromSecondaryProductsSearchBlotter from 'state/secondaryMarket/productSearch/productSearch.selectors';
import type { BdrSettlementCalendarsBlackoutDatesState } from 'state/settlementDateCalendars/settlementDateCalendars.model';
import type { AppState } from 'state/state.model';
import type { BatchWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import { getUserPreferencesLanguage } from 'state/userPreferences/userPreferences.selectors';
import type { BdrValuationCalendarsBlackoutDatesState } from 'state/valuationDateCalendars/valuationDateCalendars.model';
import * as fromAddOnManagement from 'state/workspaces/addonManagement/addonManagement.selectors';
import * as fromDynamicWorkspace from 'state/workspaces/dynamic/dynamic.selectors';
import { getPersistedTabFromEssentialWorkspaceTab } from 'state/workspaces/essential/selectors';
import type { WorkspaceType } from 'state/workspaces/model';
import * as fromSecondaryPostEventsWorkspace from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.selector';
import * as fromSecondaryQuotesWorkspace from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import * as fromStaticWorkspace from 'state/workspaces/static/static.selectors';
import type { WorkspaceTab } from 'state/workspaces/tabs/tabs.model';
import { getWorkspaceTabs } from 'state/workspaces/tabs/tabs.selector';
import {
  getWorkspaceActiveTabId,
  getWorkspaceIsEssentialTabsPanelShown,
  getWorkspaceTabIds,
} from 'state/workspaces/workspaces/workspaces.selector';

import type { ProductEditRequest } from '../../../apiConnector/secondary-market/product-search/model';

export function isInSecondaryMarketBlotterRoute(state: AppState) {
  return state.router.path === '/secondary-trade-blotter';
}

export function isInSecondaryMarketOrderBlotterRoute(state: AppState) {
  return state.router.path === '/secondary-orders-blotter';
}

export function isInScheduledQuoteBlotterRoute(state: AppState) {
  return state.router.path === '/scheduled-quotes-blotter';
}

export function isInQuoteBlotterRoute(state: AppState) {
  return state.router.path === '/quote-blotter';
}

export function isInPriceImprovementBlotterRoute(state: AppState) {
  return state.router.path === '/price-improvement-blotter';
}

export function isInTradesBlotterRoute(state: AppState) {
  return state.router.path === '/trade-blotter';
}

export function getTabIdFromNegotiationId(
  state: AppState,
  negotiationId: string,
): { screenMode?: ScreenMode; tabId?: string } {
  for (const tabId in state.static.infos) {
    if (state.static.infos[tabId].negotiationId === negotiationId) {
      return { screenMode: ScreenMode.STATIC, tabId };
    }
  }
  for (const tabId in state.dynamic.infos) {
    if (state.dynamic.infos[tabId].negotiationId === negotiationId) {
      return { screenMode: ScreenMode.DYNAMIC, tabId };
    }
  }
  return {};
}

export function getSecondaryMarketTradeBlotterColumnDefinition(state: AppState) {
  return fromSecondaryMarketTradeBlotter.getColumnDefinition(state.blotters.secondaryMarket);
}

export function getSecondaryMarketTradeBlotterFilters(state: AppState) {
  return fromSecondaryMarketTradeBlotter.getFilters(state.blotters.secondaryMarket);
}

export function getSecondaryMarketTradeBlotterPaginationInfo(state: AppState) {
  return fromSecondaryMarketTradeBlotter.getPaginationInfo(state.blotters.secondaryMarket);
}

export function getSecondaryMarketTradeBlotterPageItems(state: AppState) {
  return fromSecondaryMarketTradeBlotter.getPageItems(state.blotters.secondaryMarket);
}

export function getSecondaryMarketTradeBlotterRequest(state: AppState) {
  return fromSecondaryMarketTradeBlotter.getTradeRequest(state.blotters.secondaryMarket);
}

export function getBlotterColumnDefinition(
  state: AppState,
  blotterType: BlotterType,
): readonly IBlotterColumnDefinition<any>[] | undefined {
  switch (blotterType) {
    case 'quotes':
      return state.blotters.quotes.columnDefinition;
    case 'products':
    case 'trades':
    case 'dlpProducts':
    case 'dlpProductTrades':
      return selectTradesBlottersColumnDefinition(state, blotterType);
    default:
      return [];
  }
}

export function getQuoteBlotterFilters(state: AppState) {
  return fromQuoteBlotter.getFilters(state.blotters.quotes);
}

export function isQuoteBlotterEndDateFilterInThePast(state: AppState) {
  return fromQuoteBlotter.isEndDateInThePast(state.blotters.quotes);
}

export function getQuoteBlotterLoading(state: AppState) {
  return fromQuoteBlotter.getLoading(state.blotters.quotes);
}

export function getQuoteBlotterIsFetchingFailed(state: AppState) {
  return fromQuoteBlotter.getIsFetchingFailed(state.blotters.quotes);
}

export function getQuoteBlotterFiltersToPersist(state: AppState) {
  return fromQuoteBlotter.getFiltersToPersist(state.blotters.quotes);
}

export function getQuoteBlotterRequest(state: AppState) {
  const lang = getUserPreferencesLanguage(state);
  return fromQuoteBlotter.getQuoteBlotterRequest(state.blotters.quotes, lang);
}

export function getQuoteBlotterPreferenceState(state: AppState): IBlotterPreferenceState {
  return fromQuoteBlotter.getQuoteBlotterPreferenceState(state.blotters.quotes);
}

export function getOrderBlotterColumnDefinition(state: AppState) {
  return fromSecondaryMarketOrderBlotter.getColumnDefinition(state);
}

export function getValuationDateCalendars(state: AppState): BdrValuationCalendarsBlackoutDatesState {
  return state.valuationDateCalendars;
}

export function getSettlementDateCalendars(state: AppState): BdrSettlementCalendarsBlackoutDatesState {
  return state.settlementDateCalendars;
}

export function getMonthlyExpiryCalendars(state: AppState): BdrMonthlyExpiryDatesState {
  return state.monthlyExpiryDates;
}

export function getSecondaryMarketTradeBlotterPreferenceState(state: AppState): IBlotterPreferenceState {
  return fromSecondaryMarketTradeBlotter.getSecondaryMarketTradeBlotterPreferenceState(state.blotters.secondaryMarket);
}

export function getSecondaryMarketOrderBlotterPreferenceState(state: AppState): IBlotterPreferenceState {
  return fromSecondaryMarketOrderBlotter.getPreferenceState(state);
}

export function getSecondaryMarketProductsPreferenceState(state: AppState): IBlotterPreferenceState {
  return fromSecondaryProductsSearchBlotter.getSecondaryMarketProductsPreferenceState(state);
}

export function getSecondaryMarketProductsEditRequests(state: AppState): ProductEditRequest[] {
  return fromSecondaryProductsSearchBlotter.getProductsEditRequests(state);
}

export function getPriceImprovementBlotterFilters(state: AppState) {
  return fromPriceImprovementBlotter.getFilters(state.blotters.priceImprovement);
}

export function getBlotterType<T extends IBlotterItem = IBlotterItem>(state: AppState, item?: T): BlotterType {
  if (isInQuoteBlotterRoute(state)) {
    return 'quotes';
  }
  if (isInTradesBlotterRoute(state)) {
    const type = selectTradesBlottersType(state);
    if (type === 'dlpProducts' && item?.blotterItemType === 'DlpProductTrade') {
      return 'dlpProductTrades';
    }
    return type;
  }
  if (isInPriceImprovementBlotterRoute(state)) {
    return 'priceNegotiations';
  }
}

export function isStaticTab(state: AppState, tabId: string): boolean {
  return state.workspaces?.tabs[tabId]?.workspace === 'static';
}

export function getProductVariation(state: AppState, view: StaticProductView, product: ProductVariationBase) {
  if (isDefined(product) && !isDefined(product.productSubType) && !isDefined(product.productType)) {
    return undefined;
  }
  return selectProductDescription(state.productDescription, view, product.productSubType, product.productType);
}

export function getMergedProductDescription(
  state: AppState,
  view: StaticProductView,
  productVariations: ProductVariationBase[],
  allFieldsMetadata: Readonly<Record<string, Readonly<FieldMetadata>>>[],
  batchWorkspaceVersion: BatchWorkspaceVersion,
  mustForceAdvancedMode: boolean,
): ProductGraph {
  const descriptions = productVariations.map(product => getProductVariation(state, view, product)).filter(isDefined);
  const mergedProductDescription = mergeProductGraph(...descriptions);
  if (isEmpty(allFieldsMetadata)) {
    return mergedProductDescription;
  } else {
    return sanitizeProductDescription(
      mergedProductDescription,
      allFieldsMetadata,
      batchWorkspaceVersion,
      mustForceAdvancedMode,
    );
  }
}

export function sanitizeProductDescription(
  productDescription: ProductGraph,
  allFieldsMetadata: Readonly<Record<string, Readonly<FieldMetadata>>>[],
  batchWorkspaceVersion: BatchWorkspaceVersion,
  mustForceAdvancedMode: boolean,
): ProductGraph {
  const result = [];
  const filteredProductDescription = filterProductDescription(
    productDescription,
    batchWorkspaceVersion,
    mustForceAdvancedMode,
  );
  for (const block of filteredProductDescription) {
    const fieldIds = block.children?.map(x => x.id);
    const fieldIdsToKeep = getFieldIdsToKeep(fieldIds, allFieldsMetadata, batchWorkspaceVersion, mustForceAdvancedMode);
    if (fieldIds?.length === fieldIdsToKeep.length) {
      result.push(block);
    } else if (fieldIdsToKeep.length !== 0) {
      const fields = block.children?.filter(field => fieldIdsToKeep.includes(field.id));
      const newBlock = {
        ...block,
        children: fields,
      };
      result.push(newBlock);
    }
  }
  return result;
}

const emptyArray: readonly [] = [];

function getFieldIdsToKeep(
  fieldIds: string[] | undefined,
  allFieldsMetadata: Readonly<Record<string, Readonly<FieldMetadata>>>[],
  batchWorkspaceVersion: BatchWorkspaceVersion,
  mustForceAdvancedMode: boolean,
): readonly string[] {
  return isEmpty(fieldIds)
    ? emptyArray
    : fieldIds.filter(fieldId =>
        mustForceAdvancedMode === false
          ? allFieldsMetadata.some(
              x =>
                x !== undefined &&
                x[fieldId] !== undefined &&
                x[fieldId].isHidden === false &&
                (batchWorkspaceVersion === 'Advanced' ||
                  (batchWorkspaceVersion === 'Standard' && x[fieldId].isForAdvancedModeForBatch !== true)),
            )
          : allFieldsMetadata.some(x => x !== undefined && x[fieldId] !== undefined && x[fieldId].isHidden === false),
      );
}

function filterProductDescription(
  productDescription: ProductGraph,
  batchWorkspaceVersion: BatchWorkspaceVersion,
  mustForceAdvancedMode: boolean,
): ProductGraph {
  return batchWorkspaceVersion === 'Advanced' || mustForceAdvancedMode
    ? productDescription
    : productDescription.filter(node => node.isForAdvancedMode !== true);
}

function mergeProductGraph(...productDescriptions: ProductGraph[]): ProductGraph {
  const ids = productDescriptions.reduce((accumulated: string[], newDescription) => {
    return mergeIds(
      accumulated,
      newDescription.map(node => node.id),
    );
  }, []);

  return ids.map(id =>
    mergeProductNode(...productDescriptions.map(description => description?.find(node => node.id === id))),
  );
}

function mergeIds(first: string[], second: string[]): string[] {
  const result: string[] = [...second];
  let nextInsertionIndex = 0;
  first.forEach(value => {
    const foundIndex = result.findIndex(elem => elem === value);
    nextInsertionIndex = Math.max(nextInsertionIndex, foundIndex + 1);
    if (foundIndex < 0) {
      result.splice(nextInsertionIndex, 0, value);
    }
  });

  return result;
}

function mergeProductNode(...productNodes: (ProductNode | undefined)[]): ProductNode {
  const merged = Object.assign({}, ...productNodes);
  const children = mergeProductGraph(...productNodes.map(nodes => nodes?.children).filter(isDefined));
  return {
    ...merged,
    children: children.length === 0 ? undefined : children,
  };
}

export function getGimmicksDescription(state: AppState) {
  return getAllGimmicksDescription(state.productDescription);
}

const getPersistedTabFromWorkspaceTab = (state: AppState, tab: WorkspaceTab): PersistedTab => {
  switch (tab.workspace) {
    case 'static':
      return fromStaticWorkspace.getPersistedTabFromStaticWorkspaceTab(state, tab);
    case 'dynamic':
      return fromDynamicWorkspace.getPersistedTabFromDynamicWorkspaceTab(state, tab);
    case 'secondaryMarketQuotes':
      return fromSecondaryQuotesWorkspace.getPersistedTabFromSecondaryQuotesWorkspaceTab(state, tab);
    case 'secondaryMarketPostEvents':
      return fromSecondaryPostEventsWorkspace.getPersistedTabFromSecondaryPostEventsWorkspaceTab(state, tab);
    case 'newBatch':
      return {
        ...tab,
        tabContentType: 'primary',
        type: 'quote',
      } as PersistedTab;
    case 'essential':
      return getPersistedTabFromEssentialWorkspaceTab(state, tab);
    default:
      return { ...tab } as PersistedTab;
  }
};

export const getPersistedTabs = (state: AppState, workspace: WorkspaceType) => {
  const tabIds = getWorkspaceTabIds(state, workspace);
  const tabs = getWorkspaceTabs(state, workspace)
    .map(t => getPersistedTabFromWorkspaceTab(state, t))
    .sort((a, b) => tabIds.indexOf(a.id) - tabIds.indexOf(b.id));

  const activeTabId = getWorkspaceActiveTabId(state, workspace);
  const isEssentialTabsPanelShown = getWorkspaceIsEssentialTabsPanelShown(state, workspace);

  return {
    activeTabId: activeTabId !== undefined && tabIds.includes(activeTabId) ? activeTabId : undefined,
    tabs,
    isEssentialTabsPanelShown,
  } as PersistedTabsDescription;
};

export function getAddonManagementWorkspaceFiltersToPersist(state: AppState) {
  return fromAddOnManagement.getFiltersToPersist(state);
}

export function getAddonManagementFormsRequest(state: AppState) {
  return fromAddOnManagement.getFormsRequest(state);
}
